import Phaser from "phaser";
import { getInstructionsContent } from "../ui/instructionsContent";

export class InstructionsScene extends Phaser.Scene {
  private pageIndex = 0;
  private pagesGroup!: Phaser.GameObjects.Container;
  private pages: Phaser.GameObjects.Container[] = [];

  private prevBtn!: Phaser.GameObjects.Rectangle;
  private nextBtn!: Phaser.GameObjects.Rectangle;
  private pageIndicator!: Phaser.GameObjects.Text;

  private isAnimating = false;

  constructor() {
    super("InstructionsScene");
  }

  create(): void {
    const cam = this.cameras.main;
    const { width, height } = cam;

    cam.setBackgroundColor("#000000");

    const isNarrow = width < 420;

    const marginX = Math.max(18, width * 0.07);
    const wrapWidth = width - marginX * 2;

    // ✅ Readable, but not huge:
    const titleFontSize = isNarrow ? 30 : 34;
    const bodyFontSize = isNarrow ? 20 : 22; // <-- change this to tune readability
    const headingFontSize = bodyFontSize + 4;

    const lineGap = Math.round(bodyFontSize * 0.25);
    const sectionGap = Math.round(bodyFontSize * 0.7);
    const rowGap = Math.round(bodyFontSize * 0.55);
    const iconSize = Math.max(14, Math.round(bodyFontSize * 0.55));

    // Bottom button pinned
    const buttonWidth = Math.min(360, width * 0.82);
    const buttonHeight = isNarrow ? 62 : 66;
    const bottomPad = isNarrow ? 22 : 26;
    const buttonY = Math.round(height - bottomPad - buttonHeight / 2);

    const { title, legendRows, laserLevelsLines, controlsLines } =
      getInstructionsContent();

    // ---- Title (fixed) ----
    const titleObj = this.add.text(width / 2, 22, title, {
      fontFamily: "monospace",
      fontSize: `${titleFontSize}px`,
      color: "#ffffff",
    });
    titleObj.setOrigin(0.5, 0);

    // ---- Viewport (pages live here) ----
    const viewportTop = Math.round(titleObj.y + titleObj.height + 14);
    const navY = Math.round(buttonY - buttonHeight / 2 - 36);
    const viewportBottom = Math.round(navY - 16);
    const viewportHeight = Math.max(120, viewportBottom - viewportTop);

    // Subtle “book area” panel
    const panel = this.add.rectangle(
      width / 2,
      viewportTop + viewportHeight / 2,
      width - marginX,
      viewportHeight,
      0x0a0a10,
      0.35
    );
    panel.setOrigin(0.5);
    panel.setStrokeStyle(1, 0xffffff, 0.08);

    // Group holding all pages (we slide this left/right)
    this.pagesGroup = this.add.container(0, viewportTop);

    // Mask to clip to viewport (no overlap outside “book”)
    const maskRect = this.add.rectangle(
      width / 2,
      viewportTop + viewportHeight / 2,
      width,
      viewportHeight,
      0x000000,
      0
    );
    const mask = maskRect.createGeometryMask();
    this.pagesGroup.setMask(mask);

    const headingStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "monospace",
      fontSize: `${headingFontSize}px`,
      color: "#ffffff",
      wordWrap: { width: wrapWidth },
    };

    const bodyStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "monospace",
      fontSize: `${bodyFontSize}px`,
      color: "#ffffff",
      wordWrap: { width: wrapWidth },
      lineSpacing: lineGap,
    };

    // -------------------
    // Build Pages
    // -------------------
    this.pages = [
      this.buildLegendPage({
        width,
        marginX,
        wrapWidth,
        iconSize,
        rowGap,
        sectionGap,
        headingStyle,
        bodyStyle,
        legendRows,
      }),
      this.buildBulletsPage({
        width,
        marginX,
        heading: "Laser Levels",
        bullets: laserLevelsLines,
        sectionGap,
        headingStyle,
        bodyStyle,
      }),
      this.buildControlsPage({
        width,
        marginX,
        heading: "Controls",
        lines: controlsLines,
        sectionGap,
        headingStyle,
        bodyStyle,
      }),
    ];

    // Position each page side-by-side like a book/gallery
    this.pages.forEach((p, i) => {
      p.x = i * width;
      p.y = 0;
      this.pagesGroup.add(p);
    });

    // Start at page 0
    this.pageIndex = 0;
    this.pagesGroup.x = 0;

    // ---- Page Nav (buttons + indicator) ----
    this.createPageNav(width / 2, navY, width, bodyFontSize);

    // ---- Swipe to turn pages (horizontal only) ----
    const swipeMin = 42;

    let tracking = false;
    let startX = 0;
    let startY = 0;

    const inViewport = (p: Phaser.Input.Pointer) =>
      p.y >= viewportTop && p.y <= viewportBottom;

    const onDown = (p: Phaser.Input.Pointer) => {
      if (this.isAnimating) return;
      if (!inViewport(p)) return;
      tracking = true;
      startX = p.x;
      startY = p.y;
    };

    const onUp = (p: Phaser.Input.Pointer) => {
      if (!tracking) return;
      tracking = false;

      const dx = p.x - startX;
      const dy = p.y - startY;

      // Only treat as a page turn if it's a clear horizontal swipe
      if (Math.abs(dx) < swipeMin) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.2) return;

      if (dx < 0) this.goToPage(this.pageIndex + 1);
      else this.goToPage(this.pageIndex - 1);
    };

    this.input.on("pointerdown", onDown);
    this.input.on("pointerup", onUp);

    // ---- Back button (fixed) ----
    this.createBackButton(
      Math.round(width / 2),
      buttonY,
      buttonWidth,
      buttonHeight,
      bodyFontSize
    );

    this.refreshNav();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off("pointerdown", onDown);
      this.input.off("pointerup", onUp);
    });
  }

  // -------------------
  // Page Builders
  // -------------------

  private buildLegendPage(args: {
    width: number;
    marginX: number;
    wrapWidth: number;
    iconSize: number;
    rowGap: number;
    sectionGap: number;
    headingStyle: Phaser.Types.GameObjects.Text.TextStyle;
    bodyStyle: Phaser.Types.GameObjects.Text.TextStyle;
    legendRows: Array<{ color: number; text: string }>;
  }): Phaser.GameObjects.Container {
    const {
      width,
      marginX,
      iconSize,
      rowGap,
      sectionGap,
      headingStyle,
      bodyStyle,
      legendRows,
    } = args;

    const page = this.add.container(0, 0);
    let y = 12;

    const heading = this.add.text(marginX, y, "Legend", headingStyle).setOrigin(0, 0);
    page.add(heading);
    y += heading.height + sectionGap;

    for (const row of legendRows) {
      const square = this.add.rectangle(
        Math.round(marginX + iconSize / 2),
        Math.round(y + iconSize / 2),
        iconSize,
        iconSize,
        row.color
      );
      square.setOrigin(0.5);
      page.add(square);

      const label = this.add
        .text(Math.round(marginX + iconSize + 14), y, row.text, bodyStyle)
        .setOrigin(0, 0);
      page.add(label);

      y += Math.max(iconSize, label.height) + rowGap;
    }

    // small hint
    const hint = this.add
      .text(width / 2, y + 8, "Swipe →", {
        fontFamily: "monospace",
        fontSize: bodyStyle.fontSize as string,
        color: "#8f8f9e",
      })
      .setOrigin(0.5, 0);
    page.add(hint);

    return page;
  }

  private buildBulletsPage(args: {
    width: number;
    marginX: number;
    heading: string;
    bullets: string[];
    sectionGap: number;
    headingStyle: Phaser.Types.GameObjects.Text.TextStyle;
    bodyStyle: Phaser.Types.GameObjects.Text.TextStyle;
  }): Phaser.GameObjects.Container {
    const { width, marginX, heading, bullets, sectionGap, headingStyle, bodyStyle } =
      args;

    const page = this.add.container(0, 0);
    let y = 12;

    const h = this.add.text(marginX, y, heading, headingStyle).setOrigin(0, 0);
    page.add(h);
    y += h.height + sectionGap;

    for (const line of bullets) {
      const t = this.add.text(marginX, y, `• ${line}`, bodyStyle).setOrigin(0, 0);
      page.add(t);
      y += t.height + Math.max(6, Math.round((parseInt(bodyStyle.fontSize as string, 10) || 20) * 0.15));
    }

    const hint = this.add
      .text(width / 2, y + 10, "← Swipe or tap Next →", {
        fontFamily: "monospace",
        fontSize: bodyStyle.fontSize as string,
        color: "#8f8f9e",
      })
      .setOrigin(0.5, 0);
    page.add(hint);

    return page;
  }

  private buildControlsPage(args: {
    width: number;
    marginX: number;
    heading: string;
    lines: string[];
    sectionGap: number;
    headingStyle: Phaser.Types.GameObjects.Text.TextStyle;
    bodyStyle: Phaser.Types.GameObjects.Text.TextStyle;
  }): Phaser.GameObjects.Container {
    const { width, marginX, heading, lines, sectionGap, headingStyle, bodyStyle } =
      args;

    const page = this.add.container(0, 0);
    let y = 12;

    const h = this.add.text(marginX, y, heading, headingStyle).setOrigin(0, 0);
    page.add(h);
    y += h.height + sectionGap;

    for (const line of lines) {
      const t = this.add.text(marginX, y, line, bodyStyle).setOrigin(0, 0);
      page.add(t);
      y += t.height + Math.max(6, Math.round((parseInt(bodyStyle.fontSize as string, 10) || 20) * 0.15));
    }

    const hint = this.add
      .text(width / 2, y + 10, "Swipe ←", {
        fontFamily: "monospace",
        fontSize: bodyStyle.fontSize as string,
        color: "#8f8f9e",
      })
      .setOrigin(0.5, 0);
    page.add(hint);

    return page;
  }

  // -------------------
  // Navigation
  // -------------------

  private createPageNav(centerX: number, y: number, width: number, fontSize: number) {
    const btnW = 54;
    const btnH = 44;

    this.prevBtn = this.add
      .rectangle(centerX - 110, y, btnW, btnH, 0x1a1a24, 1)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffffff, 0.18)
      .setInteractive({ useHandCursor: true });

    const prevLabel = this.add
      .text(centerX - 110, y, "◀", {
        fontFamily: "monospace",
        fontSize: `${Math.max(20, Math.round(fontSize * 1.0))}px`,
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.nextBtn = this.add
      .rectangle(centerX + 110, y, btnW, btnH, 0x1a1a24, 1)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffffff, 0.18)
      .setInteractive({ useHandCursor: true });

    const nextLabel = this.add
      .text(centerX + 110, y, "▶", {
        fontFamily: "monospace",
        fontSize: `${Math.max(20, Math.round(fontSize * 1.0))}px`,
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.pageIndicator = this.add
      .text(centerX, y, "", {
        fontFamily: "monospace",
        fontSize: `${Math.max(16, Math.round(fontSize * 0.8))}px`,
        color: "#cfcfe0",
      })
      .setOrigin(0.5);

    const hover = (r: Phaser.GameObjects.Rectangle, over: boolean) => {
      r.setFillStyle(over ? 0x232332 : 0x1a1a24, 1);
    };

    this.prevBtn.on("pointerover", () => hover(this.prevBtn, true));
    this.prevBtn.on("pointerout", () => hover(this.prevBtn, false));
    this.nextBtn.on("pointerover", () => hover(this.nextBtn, true));
    this.nextBtn.on("pointerout", () => hover(this.nextBtn, false));

    this.prevBtn.on("pointerup", () => this.goToPage(this.pageIndex - 1));
    this.nextBtn.on("pointerup", () => this.goToPage(this.pageIndex + 1));

    // Keyboard support (optional)
    this.input.keyboard?.on("keydown-LEFT", () => this.goToPage(this.pageIndex - 1));
    this.input.keyboard?.on("keydown-RIGHT", () => this.goToPage(this.pageIndex + 1));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.removeAllListeners();
    });
  }

  private refreshNav() {
    const total = this.pages.length;
    this.pageIndicator.setText(`${this.pageIndex + 1} / ${total}`);

    const prevEnabled = this.pageIndex > 0;
    const nextEnabled = this.pageIndex < total - 1;

    this.prevBtn.setAlpha(prevEnabled ? 1 : 0.35);
    this.nextBtn.setAlpha(nextEnabled ? 1 : 0.35);

    this.prevBtn.disableInteractive();
    this.nextBtn.disableInteractive();
    if (prevEnabled) this.prevBtn.setInteractive({ useHandCursor: true });
    if (nextEnabled) this.nextBtn.setInteractive({ useHandCursor: true });
  }

  private goToPage(nextIndex: number) {
    if (this.isAnimating) return;
    if (nextIndex < 0 || nextIndex >= this.pages.length) return;
    if (nextIndex === this.pageIndex) return;

    const width = this.cameras.main.width;

    this.isAnimating = true;
    const from = -this.pageIndex * width;
    const to = -nextIndex * width;

    // “Book-ish” feel: slight easing + tiny overshoot
    this.tweens.add({
      targets: this.pagesGroup,
      x: to,
      duration: 320,
      ease: "Cubic.Out",
      onComplete: () => {
        this.pageIndex = nextIndex;
        this.isAnimating = false;
        this.refreshNav();
      },
    });

    // optional subtle emphasis
    const curr = this.pages[this.pageIndex];
    const next = this.pages[nextIndex];
    next.alpha = 0.7;
    this.tweens.add({ targets: next, alpha: 1, duration: 220, ease: "Quad.Out" });
    this.tweens.add({
      targets: curr,
      alpha: 0.85,
      duration: 160,
      yoyo: true,
      ease: "Quad.Out",
    });

    this.refreshNav();
  }

  // -------------------
  // Back Button
  // -------------------

  private createBackButton(
    x: number,
    y: number,
    buttonWidth: number,
    buttonHeight: number,
    fontSize: number
  ): void {
    const button = this.add.rectangle(x, y, buttonWidth, buttonHeight, 0xffd347);
    button.setOrigin(0.5);
    button.setStrokeStyle(2, 0x000000);
    button.setInteractive({ useHandCursor: true });

    const label = this.add.text(x, y, "BACK", {
      fontFamily: "monospace",
      fontSize: `${Math.max(18, Math.round(fontSize * 0.9))}px`,
      color: "#000000",
    });
    label.setOrigin(0.5);

    const hoverScale = 1.04;

    button.on("pointerover", () => {
      this.tweens.add({
        targets: [button, label],
        scaleX: hoverScale,
        scaleY: hoverScale,
        duration: 120,
        ease: "quad.out",
      });
    });

    button.on("pointerout", () => {
      this.tweens.add({
        targets: [button, label],
        scaleX: 1,
        scaleY: 1,
        duration: 120,
        ease: "quad.out",
      });
    });

    button.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      const ripple = this.add.circle(pointer.x, pointer.y, 4, 0xffffff, 0.4);
      this.tweens.add({
        targets: ripple,
        radius: buttonWidth,
        alpha: 0,
        duration: 220,
        ease: "quad.out",
        onComplete: () => ripple.destroy(),
      });
    });

    button.on("pointerup", () => {
      this.scene.start("MenuScene");
    });
  }
}
